import * as React from 'react'
import classnames from 'classnames'
import Radio from 'airship/patterns/Radio'
import Stack from 'airship/patterns/stack/Stack'

export interface Selectable {
  /** ID used for selection */
  id: string
  /** Label for the radio option */
  label: React.ReactNode
  /** Optional section that will be rendered when the item is selected */
  section?: React.ReactNode
  disabled?: boolean
}

export interface Props<T extends Selectable> {
  /** List of selectable items */
  items: readonly T[]
  /** The currently selected item's ID */
  selected?: T['id']
  /** Callback for updating the selected item's ID */
  onSelect: (id: T['id']) => void
  displayClasses?: string
  /** Optionally display selectable items vertically instead of horizontally */
  stack?: boolean
  /** A legend for the fieldset this component makes. */
  legend?: React.ReactNode
  /** Hides the legend visually if it only exists for accessibility */
  hideLegend?: boolean
}

export function RadioSelect<T extends Selectable>(
  props: React.PropsWithChildren<Props<T>>
): JSX.Element {
  const {items, selected, onSelect, displayClasses, stack = false} = props

  const selectedSection =
    selected && items.find((item) => item.id === selected)?.section

  const updateSelection: React.ChangeEventHandler<HTMLInputElement> = (ev) => {
    onSelect(ev.target.value)
  }

  const classes = classnames(displayClasses)
  const legendClasses = classnames({'u-hidden-visually': props.hideLegend})

  const Items = items.map((item) => {
    return (
      <Radio
        key={item.id}
        label={item.label}
        value={item.id}
        selected={selected === item.id}
        onChange={updateSelection}
        disabled={item.disabled}
      />
    )
  })

  return (
    <fieldset className={classes}>
      {props.legend && (
        <legend className={legendClasses}>{props.legend}</legend>
      )}
      <span className="radio-select__items">
        {stack ? <Stack space="xs">{Items}</Stack> : <>{Items}</>}
      </span>
      {selected && selectedSection}
    </fieldset>
  )
}
